import { Injectable } from "@angular/core";
import { HttpContext } from "@angular/common/http";
import { CommonApiService } from "./common-api.service";
import { APIURL } from "../global/constant";
import { OPTIONAL_REQUEST } from "../_helper/http-context-tokens";

@Injectable({
  providedIn: "root",
})
export class ShotlistService {
  constructor(private commonApiService: CommonApiService) { }

  // fetchShotlistDetails(shotlistId: number | string) {
  //   return this.commonApiService.get(APIURL.shotlistDetails + shotlistId);
  // }

  onGenerateShotlist(payload: any) {
    return this.commonApiService.post(APIURL.generateShotlistReq, payload);
  }

  /** POST `/shotlist/v3/generate/req` — Plan 1 breakdown. Omit `rhythm` to let the backend choose the pace. */
  onGenerateShotlistV3(payload: {
    video_id: number;
    rhythm?: "standard" | "fast" | "calm";
  }) {
    return this.commonApiService.post(APIURL.generateShotlistReqV3, payload);
  }

  /** POST `/shotlist/v3/shot/reroll/req` — queue a new picture for one shot. */
  rerollShotV3(payload: {
    shot_id: number;
    storyboard_id: number;
    user_instructions?: string;
    model_id?: number;
    variations?: 1 | 2 | 4;
    campaign_video_aspect_ratio_id?: number;
  }) {
    return this.commonApiService.post(APIURL.generateShotlistRerollV3, payload);
  }

  // onCreateShotlist(payload: any) {
  //   return this.commonApiService.post(APIURL.createShotlist, payload);
  // }

  // onDeleteShotlistShot(shotId) {
  //   return this.commonApiService.delete(APIURL.deleteShotlistShot + shotId);
  // }

  onAddEditShotlistShot(payload: any) {
    return this.commonApiService.post(APIURL.addEditShotlistShot, payload);
  }

  onAddEditShotlistScene(payload: any) {
    return this.commonApiService.post(APIURL.addEditShotlistScene, payload);
  }

  /** POST `/shotlist/save-synopsis` — update scene title, time/location, and synopsis. */
  saveSynopsis(payload: {
    scene_id: number | string;
    scene_synopsis?: string;
    scene_time?: string;
    scene_title?: string;
  }) {
    return this.commonApiService.post(APIURL.saveSynopsis, payload);
  }

  addEntityToShot(payload: {
    shot_id: number | string;
    entity_version_id: number | string;
    image_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.shotAddEntity, payload);
  }

  setShotEntityVariation(payload: {
    shot_id: number | string;
    entity_version_id: number | string;
    variation_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.shotEntitySetVariation, payload);
  }

  deleteScene(sceneId: number | string) {
    return this.commonApiService.post(APIURL.sceneDelete + sceneId, {});
  }

  deleteShot(
    shotId: number | string,
    options?: { campaign_video_aspect_ratio_id?: number | string },
  ) {
    return this.commonApiService.post(APIURL.shotDelete + shotId, {
      ...(options?.campaign_video_aspect_ratio_id != null
        ? {
            campaign_video_aspect_ratio_id: options.campaign_video_aspect_ratio_id,
          }
        : {}),
    });
  }

  /** POST `/shotlist/swap-shots` — persist shot order within a scene for a video. */
  swapShots(payload: {
    video_id: number | string;
    shot_sequence: { shot_id: number | string; shot_number: number | string }[];
  }) {
    return this.commonApiService.post(APIURL.swapShots, payload);
  }

  /** POST `/shotlist/shot-description` — add or update a shot description (omit `shot_id` when adding). */
  saveShotDescription(payload: {
    scene_id: number | string;
    shot_description: string;
    shot_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.shotDescription, payload);
  }

  /** POST `/shotlist/shot/disable-status` — include or exclude a shot from the cut at a stage. */
  updateShotDisableStatus(payload: {
    shot_id: number | string;
    is_disabled: boolean;
    stage_name: string;
    campaign_video_aspect_ratio_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.shotDisableStatus, payload);
  }

  /** POST `/shotlist/scene/disable-status` — include or exclude a scene and its shots from the cut at a stage. */
  updateSceneDisableStatus(payload: {
    scene_id: number | string;
    is_disabled: boolean;
    stage_name: string;
    campaign_video_aspect_ratio_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.sceneDisableStatus, payload);
  }

  /** POST `/shotlist/shot/create-alt-take` */
  createAltTake(payload: {
    parent_shot_id: number | string;
    setups?: string[];
    movement?: string;
    lighting?: string;
    depth_of_field?: string;
    camera_angle?: string;
    framing?: string;
    custom_instruction?: string;
  }) {
    return this.commonApiService.post(APIURL.shotCreateAltTake, payload);
  }

  // onSwapScenes(payload: any) {
  //   return this.commonApiService.post(APIURL.swapScenes, payload);
  // }

  updateviewstatusvideo(payload: any) {
    return this.commonApiService.post(APIURL.updateviewstatusvideo, payload);
  }

  shotlistproductionboard(payload: {
    video_id: number | string;
    shot_video_id: number | string;
    campaign_video_aspect_ratio_id?: number | string;
    /**
     * Scopes the board to one language — takes, approval counts and shot state
     * are all filtered by it. Omit for the master language, which is every
     * video that has no languages configured.
     */
    language_id?: number | string;
  }) {
    const { video_id, shot_video_id, ...queryFields } = payload;
    const params = this.commonApiService.getParamsFromObj(queryFields);
    return this.commonApiService.get(
      APIURL.shotlistproductionboard +
      "/" +
      video_id +
      "/" +
      shot_video_id,
      params,
    );
  }

  /**
   * GET `/shotlist/language/{videoId}` — languages configured on a video.
   *
   * Master row first, then the adapt languages. An empty array is the normal
   * state: a video only gains a language dimension once one is added, and it
   * behaves exactly as it always has until then.
   *
   * Responds `{ status: true, data }` — not the `statusCode` envelope the
   * campaign endpoints use.
   *
   * `optional: true` for callers that can live without the list (the
   * workspace): a 403 then comes back as a plain error instead of the global
   * redirect to the dashboard. Access-code guests get that 403.
   */
  getVideoLanguages(videoId: number | string, opts?: { optional?: boolean }) {
    const context = opts?.optional
      ? new HttpContext().set(OPTIONAL_REQUEST, true)
      : undefined;
    return this.commonApiService.get(
      APIURL.videoLanguages + videoId,
      undefined,
      context,
    );
  }

  /**
   * GET `/shotlist/language/catalog` — every language that can be added.
   *
   * Static per deployment, so it is worth loading once per screen rather than
   * per picker open. Same `{ status: true, data }` envelope as the list above.
   */
  getLanguageCatalog() {
    return this.commonApiService.get(APIURL.videoLanguageCatalog);
  }

  /**
   * POST `/shotlist/language/{videoId}` — add an adapt language to a video.
   *
   * Takes the ISO `code` ("hi"), not the numeric id — the only endpoint on this
   * axis that does. Idempotent, and it writes the master row too when the video
   * did not have one, which is the normal case for the first language added.
   * Responds `{ master, adapt, job_id }`, where `job_id` is the translation
   * pass it queued.
   */
  addVideoLanguage(
    videoId: number | string,
    payload: { language_code: string; master_language_code?: string },
  ) {
    return this.commonApiService.post(APIURL.videoLanguages + videoId, payload);
  }

  /**
   * POST `/shotlist/language/{videoId}/{languageId}/disable`
   *
   * Stops future work only — the sweep queues nothing, "translate everything"
   * skips it, and batch generation refuses it with a 400. Existing translations
   * still resolve and approved clips still play, so this is reversible and
   * costs nothing to undo. No body; idempotent.
   *
   * `400` on the master language, which is the source every other language
   * falls back to — don't offer the control there.
   */
  disableVideoLanguage(videoId: number | string, languageId: number | string) {
    return this.commonApiService.post(
      `${APIURL.videoLanguages}${videoId}/${languageId}/disable`,
      {},
    );
  }

  /**
   * POST `/shotlist/language/{videoId}/{languageId}/enable`
   *
   * Queues a catch-up pass automatically, because the video will have moved on
   * while the language was off. No body; idempotent.
   */
  enableVideoLanguage(videoId: number | string, languageId: number | string) {
    return this.commonApiService.post(
      `${APIURL.videoLanguages}${videoId}/${languageId}/enable`,
      {},
    );
  }

  getshotlistapprovedvideos(payload) {
    const params = this.commonApiService.getParamsFromObj(payload);
    return this.commonApiService.get(APIURL.shotlistapprovedvideos, params);
  }

  postshotlistvideoapprove(payload: any) {
    return this.commonApiService.post(
      APIURL.shotlistvideoapprove + "/" + payload.id,
      payload,
    );
  }

  videoReadyForReview(id: number | string, payload: { is_ready_for_review: boolean; reason?: string }) {
    return this.commonApiService.post(
      APIURL.shotlistVideoReadyForReview + "/" + id,
      payload,
    );
  }

  videoResetStatus(id: number | string, payload: { reason?: string }) {
    return this.commonApiService.post(
      APIURL.shotlistVideoResetStatus + "/" + id,
      payload,
    );
  }

  updateShotVideoParams(payload: {
    shot_id: number | string;
    action?: string;
    audio?: string;
    camera_move?: string;
    camera_angle?: string;
    duration?: number | string;
    dialogue?: { line?: string; expression?: string }[];
    lens?: string;
    light?: string;
    focus?: string;
    framing?: string;
    description?: string;
  }) {
    return this.commonApiService.post(APIURL.updateShotVideoParams, payload);
  }

  getShotDetail(
    videoId: string,
    shotId: string,
    options?: { campaign_video_aspect_ratio_id?: number | string },
  ) {
    const params =
      options?.campaign_video_aspect_ratio_id != null
        ? this.commonApiService.getParamsFromObj({
            campaign_video_aspect_ratio_id: options.campaign_video_aspect_ratio_id,
          })
        : undefined;
    return this.commonApiService.get(
      APIURL.shotDetail + "/" + videoId + "/" + shotId,
      params,
    );
  }

  /**
   * Complete shot detail keyed by shotId — the consolidated Motion Builder source.
   * Carries the rich setup (entities, dialogue, frames, techspecs) and, when
   * `include_all_videos` is set, the rendered takes (`all_videos`).
   * GET /shotlist/shot/detail-complete/:shot_id?storyboard_id=&include_all_videos=true
   */
  getShotDetailComplete(
    shotId: number | string,
    options: {
      storyboard_id?: number | string | null;
      campaign_video_aspect_ratio_id?: number | string | null;
      include_all_videos?: boolean;
      /**
       * Resolves dialogue and narration to this language, and scopes
       * `all_videos` to its takes. Omit for the master language.
       */
      language_id?: number | string | null;
    } = {},
  ) {
    const params = this.commonApiService.getParamsFromObj({
      storyboard_id: options.storyboard_id ?? undefined,
      campaign_video_aspect_ratio_id:
        options.campaign_video_aspect_ratio_id ?? undefined,
      include_all_videos: options.include_all_videos ? "true" : undefined,
      language_id: options.language_id ?? undefined,
    });
    return this.commonApiService.get(APIURL.shotDetailComplete + shotId, params);
  }

  /** Persist the Motion Builder Shot Setup dossier edits. */
  saveShotSetup(payload: any) {
    return this.commonApiService.post(APIURL.saveShotSetup, payload);
  }

  /** Motion Builder Edit Clip — compose an edit prompt from selected recipes. */
  applyAiVideoEdit(payload: any) {
    return this.commonApiService.post(APIURL.aiVideoEditApply, payload);
  }

  /** Poll a composer job until its `video_prompt` is ready. */
  getAiVideoEditStatus(jobId: number | string) {
    return this.commonApiService.get(APIURL.aiVideoEditStatus + jobId);
  }

  approveReadyShots(videoId: string | number) {
    return this.commonApiService.post(APIURL.approveReadyShots + videoId, {});
  }

  pendingImagesGen(storyboardId: string | number, videoId: string | number) {
    return this.commonApiService.post(APIURL.pendingImagesGen, {
      storyboard_id: storyboardId,
      video_id: videoId,
    });
  }
  getVideoDetailsForGate(videoId: string) {
    return this.commonApiService.get(APIURL.videoDetailsForGate + videoId);
  }

  createVideox(videoId: string, creationMethod: "ai" | "manual") {
    return this.commonApiService.post(APIURL.videoxCreate, {
      video_id: videoId,
      creation_method: creationMethod,
    });
  }

  createVideoxVerification(videoId: string) {
    return this.commonApiService.post(APIURL.videoxCreate, {
      video_id: videoId,
    });
  }

  /**
   * POST /autonomous-generation — batch generate across a video.
   *
   * `language_id` is NOT an on/off switch, and sending it is not the same as
   * omitting it even when the value equals the master. Sending it makes the
   * backend skip every shot with nothing translatable in it, on the grounds
   * that its existing clip already serves all languages; omitting it makes the
   * batch a plain re-roll of everything. So only an adapt language sends it —
   * see `generateLanguageId` on the caller. Sending master's id would quietly
   * turn "generate all" into "generate some" for every existing user.
   *
   * Errors are NOT auto-toasted: this endpoint has a 404 that means success
   * ("already complete in this language") and would otherwise show red. The
   * caller owns the messaging and must handle failures itself.
   */
  autonomousGeneration(
    videoId: string | number,
    modelId: string | null = null,
    variations: number = 1,
    user_instructions?: string,
    scope?: string,
    options?: {
      campaign_video_aspect_ratio_id?: number | string;
      language_id?: number | string | null;
    },
  ) {
    return this.commonApiService.post(
      APIURL.autonomousGeneration,
      {
        video_id: String(videoId),
        model_id: modelId,
        variations,
        user_instructions: user_instructions,
        scope: scope,
        ...(options?.campaign_video_aspect_ratio_id != null
          ? {
              campaign_video_aspect_ratio_id:
                options.campaign_video_aspect_ratio_id,
            }
          : {}),
        ...(options?.language_id != null
          ? { language_id: options.language_id }
          : {}),
      },
      { "X-Skip-Error-Toast": "1" },
    );
  }

  /**
   * POST `/video-production/generate-batch` — Stage 5 batch video generate.
   * Selection: `shotlist_shot_id` wins over scene/all. Scope `all` | `no-video`.
   */
  videoProductionGenerateBatch(payload: {
    video_id: number | string;
    model_id?: number | string | null;
    variations?: number;
    user_instructions?: string;
    scope?: "all" | "no-video";
    scene_id?: number | string;
    shotlist_shot_id?: number[];
    campaign_video_aspect_ratio_id?: number | string;
    language_id?: number | string | null;
  }) {
    return this.commonApiService.post(
      APIURL.videoProductionGenerateBatch,
      {
        video_id: Number(payload.video_id),
        ...(payload.model_id != null && payload.model_id !== ""
          ? { model_id: payload.model_id }
          : {}),
        ...(payload.language_id != null
          ? { language_id: payload.language_id }
          : {}),
        variations: payload.variations ?? 1,
        ...(payload.user_instructions
          ? { user_instructions: payload.user_instructions }
          : {}),
        ...(payload.scope ? { scope: payload.scope } : {}),
        ...(payload.scene_id != null ? { scene_id: payload.scene_id } : {}),
        ...(payload.shotlist_shot_id?.length
          ? { shotlist_shot_id: payload.shotlist_shot_id }
          : {}),
        ...(payload.campaign_video_aspect_ratio_id != null
          ? {
              campaign_video_aspect_ratio_id:
                payload.campaign_video_aspect_ratio_id,
            }
          : {}),
      },
      { "X-Skip-Error-Toast": "1" },
    );
  }

  /**
   * POST `/video-production/generate` — Stage 5 single-shot video generate.
   */
  videoProductionGenerate(payload: {
    shot_id: number | string;
    video_id?: number | string;
    campaign_video_aspect_ratio_id?: number | string;
    model_id?: number | string | null;
    language_id?: number | string | null;
    shot_video_id?: number | string;
    userinput?: {
      variations?: number;
      user_instructions?: string;
    };
  }) {
    return this.commonApiService.post(
      APIURL.videoProductionGenerate,
      payload,
      // Callers toast the API message themselves (or a single fallback).
      { "X-Skip-Error-Toast": "1" },
    );
  }

  /**
   * POST `/video-production/cancel` — cancel one or more in-flight
   * shot_video_request ids: `{ request_id: [12345, 12346] }`.
   */
  cancelVideoProduction(
    payload: { request_id: number | number[] },
    extraHeaders?: Record<string, string>,
  ) {
    return this.commonApiService.post(
      APIURL.videoProductionCancel,
      payload,
      extraHeaders,
    );
  }

  /**
   * GET `/autonomous-generation/process/fetchrequest?video_id=X` — batch status.
   *
   * `res.data.requests[]` (added 2026-09-03, migration
   * `2026-09-03-autonomous-request-language.sql`) is the newest batch **per
   * language this video has ever run one for** — `null` included for an
   * unscoped run — each entry carrying `language_id`, `status`, `total_shots`,
   * `completed_shots`, `failed_shots`. Absent entirely on a backend that
   * hasn't run the migration yet, so treat it as optional and degrade to "no
   * per-language status" rather than assuming it exists.
   *
   * The pre-existing top-level fields (`currentstatus`, `totalshots`, …)
   * describe ONE run — the video's overall newest, or (with `?language_id=`)
   * that language's newest — and are unchanged; the production board's status
   * modal still reads them as before.
   */
  fetchAutonomousStatus(
    videoId: string | number,
    languageId?: number | string | null,
  ) {
    const params = this.commonApiService.getParamsFromObj({
      video_id: String(videoId),
      // Sent, the top-level fields describe the newest batch FOR THAT LANGUAGE.
      // Omitted, they describe the newest batch for the video regardless of
      // language — which is why the caller must check `data.language_id` before
      // trusting them. There is deliberately no way to ask for "the unscoped
      // (NULL-language) batch" — see `statusBelongsToSelectedLanguage`.
      language_id: languageId ?? undefined,
    });
    return this.commonApiService.get(APIURL.autonomousGenerationFetchRequest, params);
  }

  getVideoJobStatus(payload: {
    job_id: number | string | (number | string)[];
  }) {
    return this.commonApiService.post(APIURL.videoJobStatus, payload);
  }

  /** POST /schedulecronjobs/video-job-cancel — same `{ job_id }` as video-job-status. */
  cancelVideoJob(
    payload: { job_id: number | string | (number | string)[] },
    extraHeaders?: Record<string, string>,
  ) {
    return this.commonApiService.post(
      APIURL.videoJobCancel,
      payload,
      extraHeaders,
    );
  }

  /**
   * Unified job-status poll. Scope by video/board, a single shot, or explicit
   * job ids. Returns `{ has_active, jobs[] }` with a normalized status per job.
   * See docs/unified-polling-backend-handoff.md.
   */
  getJobsStatus(payload: {
    video_id?: number | string;
    shot_video_id?: number | string;
    shot_id?: number | string;
    job_ids?: (number | string)[];
  }) {
    return this.commonApiService.post(APIURL.jobsStatus, payload);
  }

  /**
   * POST `/shotlist/create-element-extraction-job` — kick off (or reuse) frame
   * element extraction so requires_target edit recipes can populate their
   * "pick from the frame" target list.
   */
  createElementExtractionJob(payload: {
    video_id: number | string;
    storyboard_id: number | string;
    shot_id: number | string;
    variation_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.createElementExtractionJob, payload);
  }

  pauseAutonomousGeneration(id: string | number) {
    return this.commonApiService.post(APIURL.autonomousGenerationPause + id + '/pause', {});
  }

  resumeAutonomousGeneration(id: string | number) {
    return this.commonApiService.post(APIURL.autonomousGenerationResume + id + '/resume', {});
  }

  /** POST `/autonomous-generation/cancel-batch` — moodboard-v2 / storyboard-v2 batch cancel. */
  cancelAutonomousBatch(
    payload: {
      video_id: number | string;
      stage: "moodboard" | "storyboard" | string;
      request_id?: number | string;
    },
    extraHeaders?: Record<string, string>,
  ) {
    return this.commonApiService.post(
      APIURL.autonomousGenerationCancelBatch,
      payload,
      extraHeaders,
    );
  }

  onChangeModel(payload: any) {
    return this.commonApiService.post(APIURL.changeModel, payload);
  }

  aiVideoUploadPresign(payload: {
    shot_id: number | string;
    file_name: string;
    videox_id: number | string;
    campaign_video_aspect_ratio_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.aiVideoUploadPresign, payload);
  }

  aiVideoUploadSave(payload: {
    shot_id: number | string;
    key: string;
    file_name: string;
    public_url: string;
    videox_id: number | string;
    campaign_video_aspect_ratio_id?: number | string;
  }) {
    return this.commonApiService.post(APIURL.aiVideoUploadSave, payload);
  }

  saveVisualDna(payload: {
    video_id: string | number;
    user_instructions: string;
  }) {
    return this.commonApiService.post(APIURL.visualDnaSave, payload);
  }

  upscaleStoryboardImage(payload: {
    upscaleFactor: "x2" | "x3" | "x4";
    model_id: number | string;
    shot_id: number | string;
    storyboard_id: number | string;
    shotlist_shot_id: number | string;
    storyboard_image_id: number | string;
  }) {
    return this.commonApiService.post(APIURL.upscaleStoryboardImage, payload);
  }

  getShotlistExcelData(shotlistId: number | string) {
    return this.commonApiService.get(`${APIURL.shotlistExcelData}/${shotlistId}`);
  }
}
